/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { spacing, ticketColors, ticketFonts, typography } from '../theme/public-api';
import { SoftPressable } from './SoftPressable';
import { VueltiDurmiendo } from './VueltiDurmiendo';

export type ModuleEmptyStateProps = {
  loading?: boolean;
  loadingText?: string;
  /** Título principal (no se muestra si solo hay estado de carga). */
  title?: string;
  subtitle?: string;
  /** Kicker uppercase (solo `variant="nest"`). */
  kicker?: string;
  primaryAction?: { label: string; onPress: () => void; testID?: string };
  secondaryAction?: { label: string; onPress: () => void; testID?: string };
  /** testID para E2E (se aplica al contenedor raíz). */
  testID?: string;
  /** Muestra a Vuelti durmiendo en estados vacíos. */
  showMascot?: boolean;
  /**
   * `default` = paper card.
   * `nest` = soft-teal nest del proto Square (empty / CTA).
   */
  variant?: 'default' | 'nest';
};

/**
 * Estado vacío alineado con Catálogo (título + subtítulo + acciones opcionales).
 */
export function ModuleEmptyState({
  loading,
  loadingText = 'Cargando…',
  title,
  subtitle,
  kicker,
  primaryAction,
  secondaryAction,
  testID,
  showMascot = true,
  variant = 'default',
}: ModuleEmptyStateProps) {
  if (loading) {
    return (
      <View style={styles.emptyLoading} testID={testID}>
        <ActivityIndicator color={ticketColors.gold} size="large" />
        <Text style={styles.emptyLoadingText}>{loadingText}</Text>
      </View>
    );
  }

  const nest = variant === 'nest';

  return (
    <View style={[nest ? styles.nest : styles.emptyState]} testID={testID}>
      {showMascot ? (
        <View style={styles.mascot}>
          <VueltiDurmiendo />
        </View>
      ) : null}
      {nest && kicker ? <Text style={styles.nestKicker}>{kicker}</Text> : null}
      {title ? <Text style={[nest ? styles.nestTitle : styles.emptyTitle]}>{title}</Text> : null}
      {subtitle ? (
        <Text style={[nest ? styles.nestSubtitle : styles.emptySubtitle]}>{subtitle}</Text>
      ) : null}
      {secondaryAction ? (
        <SoftPressable
          style={[nest ? styles.nestSecondary : styles.secondaryBtn, styles.emptyAction]}
          onPress={secondaryAction.onPress}
          accessibilityRole="button"
          accessibilityLabel={secondaryAction.label}
          testID={secondaryAction.testID}
        >
          <Text style={nest ? styles.nestSecondaryText : styles.secondaryBtnText}>
            {secondaryAction.label}
          </Text>
        </SoftPressable>
      ) : null}
      {primaryAction ? (
        <SoftPressable
          style={[nest ? styles.nestCta : styles.primaryBtn, styles.emptyAction]}
          onPress={primaryAction.onPress}
          accessibilityRole="button"
          accessibilityLabel={primaryAction.label}
          testID={primaryAction.testID}
          hoverLift={false}
        >
          <Text style={nest ? styles.nestCtaText : styles.primaryBtnText}>{primaryAction.label}</Text>
        </SoftPressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  emptyLoading: {
    marginTop: spacing.xl,
    paddingVertical: spacing.xxl,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
  },
  emptyLoadingText: {
    ...typography.body,
    color: ticketColors.ink2,
  },
  emptyState: {
    marginTop: spacing.sm,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.xl,
    alignItems: 'center',
    gap: spacing.md,
  },
  mascot: {
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: ticketColors.ink,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  emptySubtitle: {
    ...typography.body,
    color: ticketColors.ink2,
    textAlign: 'center',
    lineHeight: 22,
  },
  nest: {
    marginTop: spacing.sm,
    padding: 14,
    borderRadius: 12,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandBorderSoft,
    alignItems: 'stretch',
    gap: 4,
  },
  nestKicker: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  nestTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    marginTop: 4,
  },
  nestSubtitle: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
    marginTop: 4,
  },
  nestCta: {
    marginTop: 12,
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nestCtaText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  nestSecondary: {
    marginTop: 8,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nestSecondaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  secondaryBtn: {
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  emptyAction: {
    alignSelf: 'stretch',
  },
});
